import { Panel } from '$tablet-app/src/app/components/Panel';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import Text from '$tablet-app/src/app/components/Text';
import { useGoalsInfoVideo } from '$tablet-app/src/app/videos/hooks/info_video_hooks';
import { Button } from '@akin-home/buttons/native';
import { getGoalCompletionProgress, Goal } from '@akin-home/goals';
import { spacing } from '@akin-home/theme';
import { addDays, format, isSameDay } from 'date-fns';
import React, { FC, useMemo } from 'react';
import {
  FlatList,
  ScrollView,
  ScrollViewProps,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import styled from 'styled-components/native';
import GoalCard from '../GoalCard';
import { GoalTypeFilter } from './YourGoalsScreen';

interface Props {
  /** The day currently being displayed */
  day?: Date;

  user_id?: string;

  /** The list of goals for the day */
  goals?: Goal[];

  type_filter: GoalTypeFilter;

  /** Called when day change is triggered */
  onChangeDay?: (day: Date) => void;

  onPressGoal?: (goal: Goal) => void;

  onLongPressGoal?: (goal: Goal) => void;

  onPressEditGoal?: (goal: Goal) => void;

  onPressStatistics?: () => void;

  onPressCreateGoal?: () => void;

  onChangeTypeFilter?(filter: GoalTypeFilter): void;
}

type GoalOrEmptyData = Goal | { key: string; empty: boolean };

const empty_goals: Goal[] = [];

/** State Provider for Goals Screen */
const YourGoalsScreenView: FC<Props> = (props) => {
  const {
    day,
    goals = empty_goals,
    user_id,
    type_filter,
    onChangeDay,
    onPressGoal,
    onLongPressGoal,
    onPressEditGoal,
    onPressStatistics,
    onPressCreateGoal,
    onChangeTypeFilter,
  } = props;

  const { data } = useGoalsInfoVideo();

  const { width } = useWindowDimensions();

  const numColumns = useMemo(() => calcNumColumns(width), [width]);

  const goal_data = useMemo(
    () => formatData(goals, numColumns),
    [goals, numColumns]
  );

  const info_source = useMemo(
    () => (data?.value ? { drive_id: data.value } : undefined),
    [data]
  );

  const onPressChangeFilter = (filter: GoalTypeFilter) => () =>
    onChangeTypeFilter?.(filter);

  const onPressChangeDay = (to: 'next' | 'previous') => () => {
    if (!onChangeDay || !day) return;

    if (to === 'previous') {
      onChangeDay(addDays(day, -1));
    } else {
      onChangeDay(addDays(day, 1));
    }
  };

  const getGoalProgress = (goal: Goal) =>
    day && user_id && getGoalCompletionProgress(goal, { date: day, user_id });

  const showSmallScreenStyles = width < 600;
  const showSmallCardVariant = width < 400;
  const showStatsticsButton = width >= 1000;

  return (
    <Screen>
      <ScreenHeader>
        <ScreenTitle source={info_source}>Goals</ScreenTitle>

        {showStatsticsButton ? (
          <Button
            variant="secondary"
            icon="analytics"
            onPress={onPressStatistics}
          >
            Statistics
          </Button>
        ) : null}
      </ScreenHeader>

      <Body>
        <ControlPanel isSmallScreen={showSmallScreenStyles}>
          <FilterButtons isSmallScreen={showSmallScreenStyles}>
            <Button
              variant={type_filter === 'all' ? 'primary' : 'secondary'}
              onPress={onPressChangeFilter('all')}
            >
              All
            </Button>
            <Button
              variant={type_filter === 'day' ? 'primary' : 'secondary'}
              onPress={onPressChangeFilter('day')}
            >
              Daily
            </Button>
            <Button
              variant={type_filter === 'week' ? 'primary' : 'secondary'}
              onPress={onPressChangeFilter('week')}
            >
              Weekly
            </Button>
            <Button
              variant={type_filter === 'month' ? 'primary' : 'secondary'}
              onPress={onPressChangeFilter('month')}
            >
              Monthly
            </Button>
          </FilterButtons>

          <DayButtons>
            <Button
              variant="text"
              iconSize={25}
              icon="chevron-left"
              onPress={onPressChangeDay('previous')}
              testID="previous-goals-btn"
            />

            <Text variant="h3" weight="semibold">
              {day ? getDayTitle(day) : null}
            </Text>

            <Button
              variant="text"
              iconSize={25}
              icon="chevron-right"
              onPress={onPressChangeDay('next')}
              testID="next-goals-btn"
            />
          </DayButtons>
        </ControlPanel>

        <GoalsTileContainer
          data={goal_data}
          numColumns={numColumns}
          key={numColumns}
          renderItem={({ item }) =>
            'empty' in item ? (
              <EmptyGoal key={item.key} />
            ) : (
              <GoalCard
                goal={item}
                onPressEdit={onPressEditGoal}
                progress={getGoalProgress(item) || 0}
                onPress={onPressGoal}
                onLongPress={onLongPressGoal}
                style={styles.goalCard}
                smallVariant={showSmallCardVariant}
              />
            )
          }
        />
      </Body>

      {onPressCreateGoal ? (
        <AddButtonContainer pointerEvents="box-none">
          <Button size="large" onPress={onPressCreateGoal} icon="plus">
            Create Goal
          </Button>
        </AddButtonContainer>
      ) : null}
    </Screen>
  );
};

const getDayTitle = (day: Date) => {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  const yesterday = addDays(today, -1);

  if (isSameDay(day, today)) {
    return 'Today';
  }

  if (isSameDay(day, tomorrow)) {
    return 'Tomorrow';
  }

  if (isSameDay(day, yesterday)) {
    return 'Yesterday';
  }

  return format(day, 'EEEE, d/M/yyyy');
};

const Screen = styled.View`
  flex: 1;
`;

const Body = styled.View`
  flex: 1;
  gap: 20px;
`;

const GoalsTileContainer = styled(FlatList<GoalOrEmptyData>).attrs(
  ({ numColumns = 1 }) => ({
    contentContainerStyle: {
      paddingBottom: 100,
      paddingHorizontal: 20,
      gap: 10,
      flexGrow: 1,
    },
    columnWrapperStyle:
      numColumns > 1
        ? {
            gap: 10,
            flex: 1,
          }
        : undefined,
  })
)`
  flex: 1;
  padding-bottom: 80px;
`;

const AddButtonContainer = styled.View`
  position: absolute;
  flex-direction: row;
  justify-content: center;
  bottom: 30px;
  left: 0;
  width: 100%;
`;

const EmptyGoal = styled.View`
  flex: 1;
  padding: 20px;
`;

const ControlPanel = styled(Panel)<{ isSmallScreen?: boolean }>`
  flex-direction: ${({ isSmallScreen }) => (isSmallScreen ? 'column' : 'row')};
  gap: ${spacing[2]}px;
  justify-content: space-between;
  padding: ${spacing[3]}px;
  margin: 0 20px;
`;

interface FilterButtonsProps extends ScrollViewProps {
  isSmallScreen?: boolean;
}
const FilterButtons = styled(ScrollView).attrs<FilterButtonsProps>((props) => ({
  horizontal: true,
  contentContainerStyle: StyleSheet.compose(
    {
      justifyContent: props.isSmallScreen ? 'center' : 'flex-start',
      padding: spacing[2],
      gap: 10,
      flexGrow: 1,
    },
    props.contentContainerStyle
  ),
  ...props,
}))<FilterButtonsProps>``;

const DayButtons = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 20px;
  justify-content: center;
`;

const minCols = 1;

const calcNumColumns = (width: number) => {
  const cols = width / 250;
  const colsFloor = Math.floor(cols) > minCols ? Math.floor(cols) : minCols;
  const colsMinusMargin = cols - 2 * colsFloor * 10;
  if (colsMinusMargin < colsFloor && colsFloor > minCols) return colsFloor - 1;
  else return colsFloor;
};

export default YourGoalsScreenView;

const formatData = (data: Goal[], numColumns: number): GoalOrEmptyData[] => {
  const incomplete_row_length = data.length % numColumns;

  if (incomplete_row_length === 0) return data;

  const placeholders_to_add = numColumns - incomplete_row_length;

  const formatted_data = [
    ...data,
    ...[...Array(placeholders_to_add)].map((_, idx) => ({
      key: 'empty-item-' + idx,
      empty: true,
    })),
  ];

  return formatted_data;
};

const styles = StyleSheet.create({
  // Making this a styled component breaks on mobile for some reason.
  goalCard: {
    flex: 1,
  },
});
